"use client";

import { IFormInput, IFormProps, IFormSubmit } from "@template/components/Form";
import React, { useEffect, useState } from "react";
import { Form } from "@template/components";
import TabContent from "@template/components/Tabs/TabContent";
import { TabsSectionContainer } from "@template/components/Tabs/styles.ts";
import * as S from "./styles.ts";
import DomainNameCustomSearchResults from "@template/blocks/domain/DomainNameCustomSearchResults";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { getTlds } from "@/redux/slices/store/thunks/getTlds.ts";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";
import { useSearchParams } from "next/navigation";
import { customDomainSearch } from "@/redux/slices/domains/thunks/customSearchDomains.ts";

type DomainNameTldSearchProps = {
  input?: Omit<IFormInput, "name">;
  submit?: Omit<IFormSubmit, "onSubmit">;
  tlds: { tld: number }[];
  availableTitle?: string;
  unavailableTitle?: string;
  header?: {
    title: string;
    subTitle: string;
  };
};

const DomainNameTldSearch = ({
  input,
  submit,
  tlds,
  availableTitle,
  unavailableTitle,
  header,
}: DomainNameTldSearchProps) => {
  const dispatch = useTypedDispatch();
  // Get the search params from the URL
  // This is used to pre-fill the search input if a domain is provided in the URL
  // e.g. /domain-names/uk-registration?domain=example.com
  const searchParams = useSearchParams();
  const search = searchParams.get("domain") || "";

  const availableTlds = useTypedSelector((state) => state.store.tlds.tlds);

  const [formValue, setFormValue] = useState({
    search: search ? search : "",
  });

  const inputs: IFormProps["inputs"] = [
    {
      ...input,
      type: "text",
      name: "search",
      required: true,
      hiddenRequired: true,
      autoFocus: true,
    },
  ];

  const handleChange = (vals: any) => {
    setFormValue(vals);
  };

  const { results, loading: resultLoading } = useTypedSelector(
    (state) => state.domains.search
  );
  const onSubmit = async () => {
    const { search } = formValue;
    if (!search) return;

    dispatch(customDomainSearch({ search, tlds }));
    if (searchParams.get("domain")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  // If the search params contain a domain, automatically submit the form
  // This is useful for pre-filling the search input when a user clicks a link with a domain in the URL
  // e.g. /domain-names/uk-registration?domain=example.com
  useEffect(() => {
    if (searchParams.get("domain")) {
      onSubmit();
    }
  }, []);

  useEffect(() => {
    if (!availableTlds.loaded) {
      dispatch(getTlds());
    }
  }, [availableTlds.loaded]);

  return (
    <>
      <S.Wrapper>
        <ArchStandaloneSectionComponent
          color="white"
          background="dark"
          withPadding
        />
        <TabsSectionContainer style={{ zIndex: 1 }}>
          <TabContent>
            <Form
              value={formValue}
              onChange={handleChange}
              inputs={inputs}
              simple
              submit={{
                ...submit,
                text: submit.children,
                style: {
                  width: "fit-content",
                  marginLeft: "auto",
                },
                onSubmit,
                disabled: resultLoading,
              }}
            />
          </TabContent>
        </TabsSectionContainer>
      </S.Wrapper>
      <DomainNameCustomSearchResults
        results={results}
        loading={resultLoading}
        header={{
          title: header?.title,
          subTitle: header?.subTitle,
        }}
        hidePrimary
        available={{
          title: availableTitle,
        }}
        unavailable={{
          title: unavailableTitle,
          button: {
            children: "What is Domain Backordering",
            size: "md",
            variant: "flat",
            href: "/domain-names/backorder/",
          },
        }}
      />
    </>
  );
};

export default DomainNameTldSearch;
